/* eslint-disable import/prefer-default-export */
import { FieldFunctionOptions, FieldPolicy, Reference } from '@apollo/client'

import {
  Filter,
  FilterMap,
  getFilteredData,
  getCacheKey,
  getFilters,
  getNewIncomingData,
  getPaginatedData,
} from '../utils'

import { KpiFilterInput } from '~generated/types'
import { isEmpty } from '~lib/helpers'

type IncomingKpis = {
  docs: Reference[]
  totalPages: number
  totalDocs: number
  page: number
}

type ExistingKpis = {
  docs: Reference[]
} & {
  [key: string]: Pick<IncomingKpis, 'totalPages' | 'totalDocs' | 'page'>
}

export class KpisQueryCache implements FieldPolicy {
  keyArgs: false

  merge = (existing: ExistingKpis, incoming: IncomingKpis, { variables, readField, canRead }: FieldFunctionOptions) => {
    const { filter } = variables
    const { docs: existingDocs = [], ...restExisting } = existing || {}
    const { docs: incomingDocs = [], ...restIncoming } = incoming

    const newIncomingDocs = getNewIncomingData({ incomingData: incomingDocs, existingData: existingDocs, readField })

    const docs = [...existingDocs.filter(canRead), ...newIncomingDocs].sort((docA, docB) => {
      const timeA = new Date(readField('createdAt', docA) as string).getTime()
      const timeB = new Date(readField('createdAt', docB) as string).getTime()
      return timeB - timeA
    })

    const filterKey = getCacheKey(filter)

    return {
      ...restExisting,
      [filterKey]: restIncoming,
      docs,
    }
  }

  read = (existing, options: FieldFunctionOptions) => {
    if (!existing) return undefined
    const { variables, readField } = options
    const { pagination, filter } = variables

    const filterKey = getCacheKey(filter || {})
    const filters = getFilters(filter, this.filters)

    const paginationData = existing[filterKey]

    const filteredDocs = isEmpty(filter)
      ? this.getFilteredDataWithEmptyFilters(existing.docs, readField)
      : getFilteredData({ filter, filters, data: existing.docs, readField })

    const docs = getPaginatedData({ filteredData: filteredDocs, page: paginationData?.page, limit: pagination?.limit })

    return { ...paginationData, docs }
  }

  get filters() {
    return {
      launchId: this.launchId,
      search: this.search,
    } as FilterMap<KpiFilterInput>
  }

  launchId: Filter<KpiFilterInput> = (ref, filter, readField) => {
    return readField('launchId', ref) === filter.launchId
  }

  search: Filter<KpiFilterInput> = (ref, filter, readField) => {
    const kpiTitle = readField('title', ref) as string
    const searchRegExp = new RegExp(filter.search, 'i')
    return searchRegExp.test(kpiTitle)
  }

  getFilteredDataWithEmptyFilters = (docs, readField) => docs.filter((doc) => readField('launchId', doc) === null)
}
